import { Component, EventEmitter, Output, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, transition, style, animate, query, group } from '@angular/animations';
import { DashboadService } from '@app/pages/dashboard/service/dashboad.service';
import { getMyWidgetsSliderDisplayLabel } from '@app/pages/dashboard/widgets/shared/widget-mapping.config';
import { UserWidgetsService } from '@app/shared/services/user-widgets.service';
import { UserRoleService } from '@app/core/services/user-role.service';
import { AuthService } from '@app/core/services/Auth/auth-service';
import { UserRole } from '@app/core/models/user-role.enum';
import { forkJoin } from 'rxjs';
import { Spinner } from '@app/shared/components/spinner/spinner';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { matTooltipSmartImports } from '@app/shared/imports/mat-tooltip-smart';

export interface WidgetItem {
  id: string;
  name: string;
  checked: boolean;
  /** Backend widget identifier */
  widgetName?: string;
  /** API label for display (overrides name when present) */
  label?: string;
}

@Component({
  selector: 'app-my-widgets-slider',
  standalone: true,
  imports: [CommonModule, Spinner, MatIconModule, ...matTooltipSmartImports],
  templateUrl: './my-widgets-slider.html',
  styleUrls: ['./my-widgets-slider.scss'],
  host: { '[@slider]': '' },
  animations: [
    trigger('slider', [
      transition(':enter', [
        group([
          query('.backdrop', [
            style({ opacity: 0 }),
            animate('250ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 1 }))
          ]),
          query('.my-widgets-slider-panel', [
            style({ transform: 'translateX(100%)' }),
            animate('280ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateX(0)' }))
          ])
        ])
      ]),
      transition(':leave', [
        group([
          query('.my-widgets-slider-panel', [
            animate('220ms cubic-bezier(0.4, 0, 1, 1)', style({ transform: 'translateX(100%)' }))
          ], { optional: true }),
          query('.backdrop', [
            animate('200ms cubic-bezier(0.4, 0, 1, 1)', style({ opacity: 0 }))
          ], { optional: true })
        ])
      ])
    ])
  ]
})
export class MyWidgetsSlider implements OnInit {
  /** Roles that see all widgets (from all-widgets API). Others see only their assigned widgets (from user-widgets). */
  private static readonly ROLES_SEE_ALL_WIDGETS: UserRole[] = [
    UserRole.DEPARTMENT_HEAD,
    UserRole.INTL_MANAGEMENT,
    UserRole.CAN_MANAGEMENT
  ];
  @Output() close = new EventEmitter<void>();

  private dashboardService = inject(DashboadService);
  private userWidgetsService = inject(UserWidgetsService);
  private userRoleService = inject(UserRoleService);
  private authService = inject(AuthService);
  private toastr = inject(ToastrService);

  widgets = signal<WidgetItem[]>([]);
  /** Initial checked widget IDs when slider opened (for diff on Apply) */
  private initialCheckedIds = new Set<string>();
  loading = signal(false);
  applying = signal(false);
  allChecked = signal(false);

  selectedWidgetsCount = computed(() => this.widgets().filter(w => w.checked).length);

  ngOnInit(): void {
    this.loadWidgets();
  }

  private get showAllWidgets(): boolean {
    return MyWidgetsSlider.ROLES_SEE_ALL_WIDGETS.includes(this.userRoleService.currentRole());
  }

  private loadWidgets(): void {
    this.loading.set(true);
    if (this.showAllWidgets) {
      this.loadForRolesWithAllWidgets();
    } else {
      this.loadForMemberOrPM();
    }
  }

  /** Department heads etc: list from all-widgets, checked state from user-widgets */
  private loadForRolesWithAllWidgets(): void {
    this.dashboardService.getAllWidgets().pipe(take(1)).subscribe({
      next: (res: any) => {
        const raw = this.normalizeWidgetsResponse(res);
        this.dashboardService.getUserWidgets().pipe(take(1)).subscribe({
          next: (userRes: any) => {
            const userWidgetIds = this.getUserWidgetIds(userRes);
            this.initialCheckedIds = new Set(userWidgetIds);
            const items = this.buildWidgetItems(raw, userWidgetIds);
            this.widgets.set(items);
            this.updateAllChecked();
            this.loading.set(false);
          },
          error: () => {
            this.initialCheckedIds = new Set();
            const items = this.buildWidgetItems(raw, []);
            this.widgets.set(items);
            this.updateAllChecked();
            this.loading.set(false);
          }
        });
      },
      error: (err) => {
        this.toastr.error('Failed to load widgets');
        this.loading.set(false);
      }
    });
  }

  /** Member, PM etc: list and checked state both from user-widgets (their assigned widgets) */
  private loadForMemberOrPM(): void {
    this.dashboardService.getUserWidgets().pipe(take(1)).subscribe({
      next: (res: any) => {
        const raw = this.normalizeWidgetsResponse(res);
        const userWidgetIds = this.getUserWidgetIds(res);
        this.initialCheckedIds = new Set(userWidgetIds);
        const items = this.buildWidgetItems(raw, userWidgetIds);
        this.widgets.set(items);
        this.updateAllChecked();
        this.loading.set(false);
      },
      error: (err) => {
        this.toastr.error('Failed to load widgets');
        this.loading.set(false);
      }
    });
  }

  /** Build slider items. Uses checked from API when present, else inferred from checkedIds. Deduplicates by id. */
  private buildWidgetItems(raw: any[], checkedIds: string[]): WidgetItem[] {
    const seen = new Set<string>();
    const items: WidgetItem[] = [];
    for (const w of raw) {
      const id = w._id ?? w.id ?? w.widgetName ?? w.name ?? w.type ?? String(w);
      if (seen.has(id)) continue;
      seen.add(id);
      const name = w.name ?? w.widgetName ?? w.label ?? id;
      const widgetName = w.widgetName ?? w.name ?? w.type ?? id;
      const checked = w.checked !== undefined ? !!w.checked : (w.isChecked !== undefined ? !!w.isChecked : checkedIds.includes(id));
      const label = getMyWidgetsSliderDisplayLabel({
        widgetName: w.widgetName,
        name: w.name,
        label: w.label,
        type: w.type,
        componentKey: w.componentKey
      });
      items.push({ id, name, checked, widgetName, label });
    }
    return items;
  }

  private normalizeWidgetsResponse(res: any): any[] {
    if (Array.isArray(res)) return res;
    if (res?.data && Array.isArray(res.data)) return res.data;
    if (res?.data && typeof res.data === 'object') return Object.values(res.data).filter((x: any) => Array.isArray(x)).flat();
    if (res?.widgets && Array.isArray(res.widgets)) return res.widgets;
    return [];
  }

  /** IDs of widgets with checked: true when API provides it; otherwise all widget IDs. */
  private getUserWidgetIds(res: any): string[] {
    const raw = this.normalizeWidgetsResponse(res);
    const hasChecked = raw.some((w: any) => 'checked' in w || 'isChecked' in w);
    const filtered = hasChecked ? raw.filter((w: any) => w?.checked === true || w?.isChecked === true) : raw;
    return filtered.map((w: any) => w._id ?? w.id ?? w.widgetName ?? w.name ?? w.type ?? String(w));
  }

  onClose(): void {
    this.close.emit();
  }

  onToggleAll(): void {
    const checked = !this.allChecked();
    // Never allow clearing every widget — keep at least one selected
    if (!checked) {
      this.toastr.warning('Keep at least one widget selected');
      return;
    }
    this.widgets.update(list => list.map(w => ({ ...w, checked })));
    this.allChecked.set(checked);
  }

  onToggleWidget(item: WidgetItem): void {
    // Prevent unticking the last selected widget
    if (item.checked && this.selectedWidgetsCount() <= 1) {
      this.toastr.warning('Keep at least one widget selected');
      return;
    }
    this.widgets.update(list =>
      list.map(w => (w.id === item.id ? { ...w, checked: !w.checked } : w))
    );
    this.updateAllChecked();
  }

  private updateAllChecked(): void {
    const list = this.widgets();
    this.allChecked.set(list.length > 0 && list.every(w => w.checked));
  }

  onApply(): void {
    const currentUserId = this.authService.getUser()?._id ?? localStorage.getItem('userID');
    if (!currentUserId) {
      this.toastr.error('User session not found');
      return;
    }
    if (this.selectedWidgetsCount() < 1) {
      this.toastr.warning('Keep at least one widget selected');
      return;
    }
    const list = this.widgets();
    const changed = list.filter(
      w => this.initialCheckedIds.has(w.id) !== w.checked
    );
    if (changed.length === 0) {
      this.onClose();
      return;
    }
    this.applying.set(true);
    const requests = changed.map(w =>
      this.dashboardService.updateWidgetUserStatus(currentUserId, w.id, w.checked)
    );
    forkJoin(requests).pipe(take(1)).subscribe({
      next: () => {
        this.userWidgetsService.notifyWidgetsUpdated();
        this.toastr.success('Widgets updated successfully');
        this.applying.set(false);
        this.onClose();
      },
      error: (err) => {
        this.toastr.error(err?.error?.message ?? 'Failed to update widgets');
        this.applying.set(false);
      }
    });
  }

  onCancel(): void {
    this.onClose();
  }

  onReset(): void {
    const currentUserId = this.authService.getUser()?._id ?? localStorage.getItem('userID');
    if (!currentUserId) {
      this.toastr.error('User session not found');
      return;
    }
    this.applying.set(true);
    this.dashboardService.resetWidgets(currentUserId).pipe(take(1)).subscribe({
      next: () => {
        this.userWidgetsService.notifyWidgetsUpdated();
        this.toastr.success('Widgets reset successfully');
        this.applying.set(false);
        this.onClose();
      },
      error: (err) => {
        this.toastr.error(err?.error?.message ?? 'Failed to reset widgets');
        this.applying.set(false);
      }
    });
  }
}
